import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanType,
  CustomerBan,
  OrderBanPreview,
  createBansFromOrder,
  createCustomerBan,
  deactivateCustomerBan,
  listCustomerBans,
  previewOrderForBan,
} from '../api/customerBans';
import { OrdersSectionNav } from '../components/orders/OrdersSectionNav';
import { ensureApiAuthenticated } from '../services/api';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

const ALL_TYPES: BanType[] = ['name', 'email', 'address'];

const CustomerBansPage: React.FC = () => {
  const [bans, setBans] = useState<CustomerBan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [manualType, setManualType] = useState<BanType>('email');
  const [manualValue, setManualValue] = useState('');
  const [manualLine1, setManualLine1] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualPostal, setManualPostal] = useState('');
  const [manualCountry, setManualCountry] = useState('US');
  const [manualReason, setManualReason] = useState('');

  const [orderIdInput, setOrderIdInput] = useState('');
  const [preview, setPreview] = useState<OrderBanPreview | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<BanType[]>([]);
  const [fromOrderReason, setFromOrderReason] = useState('');

  const loadBans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await ensureApiAuthenticated();
      const rows = await listCustomerBans(true);
      setBans(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bans');
      setBans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBans();
  }, [loadBans]);

  const handleCreateManual = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    try {
      await ensureApiAuthenticated();
      if (manualType === 'address') {
        await createCustomerBan({
          ban_type: 'address',
          display_value: [manualLine1, manualCity, manualPostal, manualCountry]
            .filter(Boolean)
            .join(', '),
          address_json: {
            line1: manualLine1,
            city: manualCity,
            postal_code: manualPostal,
            country: manualCountry,
          },
          reason: manualReason || undefined,
        });
      } else {
        await createCustomerBan({
          ban_type: manualType,
          display_value: manualValue,
          reason: manualReason || undefined,
        });
      }
      setManualValue('');
      setManualLine1('');
      setManualCity('');
      setManualPostal('');
      setManualReason('');
      setMessage('Ban created');
      await loadBans();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ban');
    }
  };

  const handlePreviewOrder = async () => {
    setMessage(null);
    setError(null);
    setPreview(null);
    const id = orderIdInput.trim();
    if (!id) {
      setError('Enter a SyncBase order UUID');
      return;
    }
    try {
      await ensureApiAuthenticated();
      const data = await previewOrderForBan(id);
      setPreview(data);
      setSelectedTypes(data.available_ban_types);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Order preview failed');
    }
  };

  const handleBanFromOrder = async () => {
    if (!preview) return;
    setMessage(null);
    setError(null);
    try {
      await ensureApiAuthenticated();
      const result = await createBansFromOrder({
        order_id: preview.order_id,
        ban_types: selectedTypes,
        reason: fromOrderReason || undefined,
      });
      const createdCount = result.created.length;
      const skippedCount = result.skipped.length;
      setMessage(
        `Created ${createdCount} ban(s)` +
          (skippedCount ? `; skipped ${skippedCount}` : ''),
      );
      setFromOrderReason('');
      await loadBans();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to ban from order');
    }
  };

  const handleDeactivate = async (banId: string) => {
    setMessage(null);
    setError(null);
    try {
      await ensureApiAuthenticated();
      await deactivateCustomerBan(banId);
      setMessage('Ban deactivated');
      await loadBans();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate ban');
    }
  };

  const toggleType = (t: BanType) => {
    setSelectedTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Customer Ban List</h1>
          <p style={styles.subtitle}>
            Name, email, or address bans. Matching orders are held in SyncBase for review.
          </p>
          <OrdersSectionNav />
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {message && <div style={styles.message}>{message}</div>}

      <div style={styles.grid}>
        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Add manually</h2>
          <form onSubmit={handleCreateManual} style={styles.form}>
            <label style={styles.label}>
              Type
              <select
                value={manualType}
                onChange={(e) => setManualType(e.target.value as BanType)}
                style={styles.input}
              >
                {ALL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            {manualType === 'address' ? (
              <>
                <label style={styles.label}>
                  Street
                  <input
                    value={manualLine1}
                    onChange={(e) => setManualLine1(e.target.value)}
                    style={styles.input}
                    required
                  />
                </label>
                <label style={styles.label}>
                  City
                  <input
                    value={manualCity}
                    onChange={(e) => setManualCity(e.target.value)}
                    style={styles.input}
                    required
                  />
                </label>
                <label style={styles.label}>
                  Postal code
                  <input
                    value={manualPostal}
                    onChange={(e) => setManualPostal(e.target.value)}
                    style={styles.input}
                    required
                  />
                </label>
                <label style={styles.label}>
                  Country
                  <input
                    value={manualCountry}
                    onChange={(e) => setManualCountry(e.target.value)}
                    style={styles.input}
                    required
                  />
                </label>
              </>
            ) : (
              <label style={styles.label}>
                Value
                <input
                  value={manualValue}
                  onChange={(e) => setManualValue(e.target.value)}
                  style={styles.input}
                  required
                />
              </label>
            )}
            <label style={styles.label}>
              Reason (optional)
              <input
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                style={styles.input}
              />
            </label>
            <button type="submit" style={styles.primaryButton}>
              Add ban
            </button>
          </form>
        </section>

        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Add by order ID</h2>
          <div style={styles.form}>
            <label style={styles.label}>
              SyncBase order UUID
              <input
                value={orderIdInput}
                onChange={(e) => setOrderIdInput(e.target.value)}
                style={styles.input}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
            </label>
            <button type="button" style={styles.secondaryButton} onClick={handlePreviewOrder}>
              Preview
            </button>
            {preview && (
              <div style={styles.preview}>
                <div>
                  <strong>{preview.external_id}</strong> ({preview.source})
                </div>
                <div>Name: {preview.buyer_name || '—'}</div>
                <div>Email: {preview.buyer_email || '—'}</div>
                <div>
                  Address:{' '}
                  {preview.shipping_address ||
                    (preview.shipping_address_json
                      ? [
                          preview.shipping_address_json.line1,
                          preview.shipping_address_json.city,
                          preview.shipping_address_json.postal_code,
                          preview.shipping_address_json.country,
                        ]
                          .filter(Boolean)
                          .join(', ')
                      : '—')}
                </div>
                <div style={styles.checkboxRow}>
                  {preview.available_ban_types.map((t) => (
                    <label key={t} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(t)}
                        onChange={() => toggleType(t)}
                      />
                      {t}
                    </label>
                  ))}
                </div>
                <label style={styles.label}>
                  Reason (optional)
                  <input
                    value={fromOrderReason}
                    onChange={(e) => setFromOrderReason(e.target.value)}
                    style={styles.input}
                  />
                </label>
                <button
                  type="button"
                  style={styles.primaryButton}
                  onClick={handleBanFromOrder}
                  disabled={selectedTypes.length === 0}
                >
                  Ban selected fields
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      <section style={{ ...styles.card, marginTop: spacing.lg }}>
        <h2 style={styles.cardTitle}>Active bans {loading ? '(loading…)' : `(${bans.length})`}</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Value</th>
              <th style={styles.th}>Reason</th>
              <th style={styles.th}>Source order</th>
              <th style={styles.th}>Created</th>
              <th style={styles.th} />
            </tr>
          </thead>
          <tbody>
            {bans.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} style={styles.tdMuted}>
                  No active bans
                </td>
              </tr>
            ) : (
              bans.map((ban) => (
                <tr key={ban.id}>
                  <td style={styles.td}>{ban.ban_type}</td>
                  <td style={styles.td}>{ban.display_value}</td>
                  <td style={styles.td}>{ban.reason || '—'}</td>
                  <td style={styles.td}>
                    {ban.source_order_id ? (
                      <Link to="/orders/combined" style={styles.link}>
                        {ban.source_order_id.slice(0, 8)}…
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={styles.td}>{formatInstant(ban.created_at)}</td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      style={styles.dangerButton}
                      onClick={() => handleDeactivate(ban.id)}
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: spacing.lg,
  },
  header: {
    marginBottom: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typographyFlat.fontWeight.bold,
    color: colors.text,
  },
  subtitle: {
    margin: `${spacing.xs} 0 0`,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.lg,
  },
  card: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
  },
  cardTitle: {
    margin: `0 0 ${spacing.md}`,
    fontSize: typography.h2.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  input: {
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
  },
  primaryButton: {
    alignSelf: 'flex-start',
    padding: `${spacing.xs} ${spacing.md}`,
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    alignSelf: 'flex-start',
    padding: `${spacing.xs} ${spacing.md}`,
    backgroundColor: colors.backgroundAlt,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
  },
  dangerButton: {
    padding: `${spacing.xs} ${spacing.sm}`,
    backgroundColor: 'transparent',
    color: colors.error,
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
  },
  preview: {
    marginTop: spacing.sm,
    padding: spacing.md,
    backgroundColor: colors.backgroundAlt,
    borderRadius: borderRadius.md,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    fontSize: typography.small.fontSize,
    color: colors.text,
  },
  checkboxRow: {
    display: 'flex',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    textTransform: 'capitalize' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: `${spacing.sm} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
  },
  td: {
    padding: `${spacing.sm} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    verticalAlign: 'top' as const,
  },
  tdMuted: {
    padding: spacing.md,
    color: colors.textSecondary,
    textAlign: 'center' as const,
  },
  link: {
    color: colors.primary,
    textDecoration: 'none',
    fontFamily: 'monospace',
  },
  error: {
    marginBottom: spacing.md,
    padding: spacing.sm,
    backgroundColor: `${colors.error}14`,
    color: colors.error,
    borderRadius: borderRadius.sm,
  },
  message: {
    marginBottom: spacing.md,
    padding: spacing.sm,
    backgroundColor: `${colors.success}14`,
    color: colors.success,
    borderRadius: borderRadius.sm,
  },
};

export default CustomerBansPage;
